/**
 * A FILA — o que este aparelho mudou e o banco ainda não sabe.
 *
 * Igual à piscicultura: NADA sobe sozinho. A pessoa aperta «Sincronizar» e vê
 * o contador de pendentes zerar. Decisão do gestor (03/10/2026): o botão torna
 * a fila visível — quem usa aprende que o app guarda primeiro e manda depois,
 * e na hora do acerto sabe o que já foi.
 *
 * ORDEM: primeiro sobe (produtores, depois fichas — a ficha cita o produtor),
 * depois desce o que mudou no banco desde a última vez.
 *
 * O que pode acontecer com cada ficha que sobe:
 *  - aceita          → guarda a rev nova e sai da fila;
 *  - reenvio         → (R1A) a resposta anterior se perdeu no sinal fraco e o
 *                      banco já tinha esta ficha: o servidor reconhece pela
 *                      lista `enviosSemResposta` e responde ok. Não é conflito;
 *  - conflito        → outro aparelho gravou antes: as pesagens (e lotes,
 *                      porteiras, gastos, parcelas, histórico) das duas
 *                      versões se JUNTAM por id e sobem; só o que diverge
 *                      (o mesmo campo mudado nos dois) vai para «Conflitos»,
 *                      para a pessoa escolher. A «versão deste aparelho» é a
 *                      ficha no momento da resposta — com o que foi pesado
 *                      enquanto ela subia. Nada some;
 *  - número repetido → aparelho novo que recomeçou a contar: ganha o próximo
 *                      número livre e tenta de novo;
 *  - recusa de vez   → 4xx que não é conflito nem espera (ex.: Consulta, ficha
 *                      de outro): SAI da fila e fica neste aparelho marcada
 *                      «recusada pelo servidor», com a frase (3E, como já
 *                      faziam pagamentos, extrato e catira). Antes ficava
 *                      «2 para enviar» para sempre. Nada se apaga: o gestor
 *                      pode trazer para ele a ficha presa (src/dados/adocao.ts);
 *  - erro do servidor (5xx) → fica na fila e tenta de novo no próximo botão;
 *  - sem sinal       → para tudo, nada se perde; tenta de novo depois.
 */
import { acertarSequencia, enxugarAoSair as enxugarFichas, lerEstado, mudar, proximoNumero } from './armazem'
import { enxugarArquivosAoSair, sincronizarArquivos } from './arquivos'
import { enxugarExtratoAoSair, lerExtrato, sincronizarExtrato } from './extrato'
import { api, Recusa, SemSinal } from './sessao'
import { enxugarPagamentosAoSair, lerPagamentos, quantosPendentes, sincronizarPagamentos } from './pagamentos'
import { enxugarCatiraAoSair, lerCatira, sincronizarCatira } from './catira'
import { enxugarNotasAoSair, lerNotas, sincronizarNotas } from './notas'
import { agora } from '../dominio/numeros'
import { aplicarEscolhas, juntarFichas, mesmoConteudo } from '../dominio/juntar'
import type { Ficha, Produtor } from '../dominio/tipos'

export interface ResultadoDaSincronia {
  subiram: number
  desceram: number
  conflitos: number
  recusas: string[]
  semSinal: boolean
}

/** O documento que vai ao banco: a ficha sem as marcas que só fazem sentido no aparelho. */
function documentoDa(f: Ficha): Omit<Ficha, 'revServidor' | 'enviosSemResposta'> {
  const { revServidor: _r, enviosSemResposta: _e, ...doc } = f
  return doc
}

/**
 * R1A · «Sair» (src/dados/sessao.ts): os dados sigilosos da pessoa que já
 * estão no banco saem do aparelho — comissões, pagamentos, preço de venda,
 * extrato, catira, notas e as fotos. Fica só o que ainda não subiu.
 */
export async function enxugarAoSair(): Promise<void> {
  enxugarPagamentosAoSair()
  enxugarExtratoAoSair()
  enxugarCatiraAoSair()
  enxugarNotasAoSair()
  await enxugarArquivosAoSair()
  enxugarFichas()
}

/** R1A · Quantos registros estão esperando o «Sincronizar», somando todos os armazéns. */
export function contarPendentes(): number {
  const e = lerEstado()
  return e.pendentes.fichas.length + e.pendentes.produtores.length + lerExtrato().pendentes.length
    + quantosPendentes(lerPagamentos()) + lerCatira().pendentes.length + lerNotas().pendentes.length
}

function dadosDo(p: Produtor): Omit<Produtor, 'ativo'> {
  const { ativo: _a, ...dados } = p
  return dados
}

let rodando = false

export async function sincronizar(): Promise<ResultadoDaSincronia> {
  const r: ResultadoDaSincronia = { subiram: 0, desceram: 0, conflitos: 0, recusas: [], semSinal: false }
  if (rodando) return r
  rodando = true
  try {
    await subirProdutores(r)
    await subirFichas(r)
    await descer(r)
    // 2F: despesas e vales do extrato sobem no mesmo botão (src/dados/extrato.ts)
    await sincronizarExtrato(r)
    // 2D: comissão e quitação depois das fichas (apontam para a ficha no banco)
    await sincronizarPagamentos(r)
    // 2G: os arquivos por último — só sobem pendurados em registro que já está no banco
    await sincronizarArquivos(r)
    // D3: movimentos da catira por último (a entrada e a venda apontam para a ficha no banco)
    await sincronizarCatira(r)
    // D4: notas por último (ligam-se à ficha, à despesa e ao movimento da catira); o arquivo da nota vai logo atrás
    await sincronizarNotas(r)
  } catch (e) {
    if (e instanceof SemSinal) r.semSinal = true
    else throw e
  } finally {
    rodando = false
  }
  return r
}

async function subirProdutores(r: ResultadoDaSincronia): Promise<void> {
  for (const id of [...lerEstado().pendentes.produtores]) {
    const p = lerEstado().produtores.find((x) => x.id === id)
    if (!p) { tirarDaFila('produtores', id); continue }
    try {
      await api('produtores', { id: p.id, nome: p.nome, doc: p.doc, dados: dadosDo(p), ativo: p.ativo !== false })
      mudar((e) => {
        e.pendentes.produtores = e.pendentes.produtores.filter((y) => y !== id)
        delete e.recusadas.produtores[id]
      }, { doServidor: true })
      r.subiram++
    } catch (e) {
      if (!(e instanceof Recusa)) throw e
      r.recusas.push(`Produtor ${p.nome}: ${e.message}`)
      if (recusaDeVez(e)) marcarRecusa('produtores', id, e.message)
    }
  }
}

async function subirFichas(r: ResultadoDaSincronia): Promise<void> {
  for (const id of [...lerEstado().pendentes.fichas]) {
    let tentativas = 0
    while (tentativas++ < 3) {
      const f = lerEstado().fichas.find((x) => x.id === id)
      if (!f) { tirarDaFila('fichas', id); break }
      // R1A: anota ANTES de mandar. Se a resposta se perder no caminho, o
      // próximo envio diz ao servidor «a versão que você tem pode ser minha»
      const envios = [...(f.enviosSemResposta ?? []).filter((x) => x !== f.alteradaEm), f.alteradaEm].slice(-20)
      mudar((e) => {
        const x = e.fichas.find((y) => y.id === id)
        if (x) x.enviosSemResposta = envios
      }, { doServidor: true })
      try {
        const resp = await api<{ rev: number }>('fichas', {
          id: f.id, numero: f.numero, data: f.data, situacao: f.situacao, documento: documentoDa(f), rev_base: f.revServidor ?? 0,
          envios,
        })
        // só marca como enviada se ninguém mexeu na ficha enquanto ela subia
        mudar((e) => {
          const x = e.fichas.find((y) => y.id === id)
          if (x) { x.revServidor = resp.rev; delete x.enviosSemResposta }
          if (x && x.alteradaEm === f.alteradaEm) e.pendentes.fichas = e.pendentes.fichas.filter((y) => y !== id)
          delete e.recusadas.fichas[id]
        }, { doServidor: true })
        r.subiram++
        break
      } catch (e) {
        if (!(e instanceof Recusa)) throw e
        const dados = e.dados ?? {}
        if (e.status === 409 && dados['erro'] === 'conflito') {
          const precisaSubir = juntarConflito(id, dados['documento'] as Ficha, Number(dados['rev']), r)
          // a junta (pesagens dos dois lados) sobe já, no mesmo «Sincronizar»
          if (precisaSubir) continue
          break
        }
        if (e.status === 409 && dados['erro'] === 'numero_repetido') {
          renumerar(id, String(dados['maior'] ?? ''))
          continue
        }
        r.recusas.push(`Ficha ${f.numero}: ${e.message}`)
        if (recusaDeVez(e)) marcarRecusa('fichas', id, e.message)
        break
      }
    }
  }
}

/**
 * R1A · Conflito de verdade: outro aparelho gravou antes. Junta a versão do
 * banco com a DESTE aparelho NO MOMENTO DA RESPOSTA (o que foi pesado enquanto
 * a ficha subia também conta): pesagens, lotes, porteiras, gastos, parcelas e
 * histórico das duas, por id. O que diverge fica em «Conflitos» para a pessoa
 * escolher; enquanto isso vale o do banco. Devolve se a junta tem algo que o
 * banco não tem (precisa subir).
 */
function juntarConflito(id: string, doBanco: Ficha, rev: number, r: ResultadoDaSincronia): boolean {
  let precisaSubir = false
  mudar((e) => {
    const i = e.fichas.findIndex((x) => x.id === id)
    const local = i >= 0 ? e.fichas[i]! : undefined
    const banco: Ficha = { ...doBanco, id }
    if (!local) {
      e.fichas.push({ ...banco, revServidor: rev })
      e.pendentes.fichas = e.pendentes.fichas.filter((y) => y !== id)
      return
    }
    const { junta, divergencias, mudouDoBanco } = juntarFichas(banco, local)
    const nova: Ficha = { ...junta, revServidor: rev, alteradaEm: mudouDoBanco ? agora() : banco.alteradaEm }
    delete nova.enviosSemResposta
    e.fichas[i] = nova
    precisaSubir = mudouDoBanco
    if (mudouDoBanco) { if (!e.pendentes.fichas.includes(id)) e.pendentes.fichas.push(id) }
    else e.pendentes.fichas = e.pendentes.fichas.filter((y) => y !== id)
    // um conflito por ficha: o novo substitui o antigo (a versão daqui é a mais recente)
    e.conflitos = e.conflitos.filter((c) => c.local.id !== id)
    if (divergencias.length) {
      e.conflitos.push({ local: structuredClone(local), em: agora(), divergencias })
      r.conflitos++
    }
  }, { doServidor: true })
  return precisaSubir
}

/** Aparelho novo recomeçou a contar: pula para depois do maior número que o banco já tem. */
function renumerar(id: string, maior: string): void {
  mudar((e) => {
    const m = maior.match(/(\d{4})-(\d+)$/)
    if (m) e.seq[m[1]!] = Math.max(e.seq[m[1]!] ?? 0, Number(m[2]))
    const f = e.fichas.find((x) => x.id === id)
    if (f) {
      const antigo = f.numero
      f.numero = proximoNumero(e, f.data)
      f.historico.push({ em: agora(), oque: `Número trocado de ${antigo} para ${f.numero} (já estava em uso)` })
    }
  }, { doServidor: true })
}

async function descer(r: ResultadoDaSincronia): Promise<void> {
  const desde = lerEstado().ultimaSync
  const q = desde ? `?desde=${encodeURIComponent(desde)}` : ''
  const [p, f] = await Promise.all([
    api<{ agora: string; produtores: { id: string; dados: Produtor; ativo: boolean }[] }>(`produtores${q}`),
    api<{ agora: string; fichas: { id: string; rev: number; documento: Ficha }[] }>(`fichas${q}`),
  ])
  mudar((e) => {
    for (const linha of p.produtores) {
      if (e.pendentes.produtores.includes(linha.id)) continue // a mudança daqui ainda vai subir
      // 3E: a mudança daqui foi recusada — fica à vista, marcada (o gestor pode trazê-la para ele)
      if (linha.id in e.recusadas.produtores) continue
      const novo = { ...linha.dados, id: linha.id, ativo: linha.ativo }
      const i = e.produtores.findIndex((x) => x.id === linha.id)
      if (i >= 0) e.produtores[i] = novo
      else e.produtores.push(novo)
      r.desceram++
    }
    for (const linha of f.fichas) {
      if (e.pendentes.fichas.includes(linha.id)) continue
      const i = e.fichas.findIndex((x) => x.id === linha.id)
      if (i >= 0 && (e.fichas[i]!.revServidor ?? 0) >= linha.rev) continue // já tenho esta
      const nova = { ...linha.documento, id: linha.id, revServidor: linha.rev }
      // 3E: a versão daqui tinha sido recusada e o banco tem uma mais nova — a
      // do banco passa a valer e a daqui vai para «Conflitos», à vista (nada some)
      if (i >= 0 && linha.id in e.recusadas.fichas) {
        e.conflitos.push({ local: e.fichas[i]!, em: agora() })
        delete e.recusadas.fichas[linha.id]
      }
      if (i >= 0) e.fichas[i] = nova
      else e.fichas.push(nova)
      r.desceram++
    }
    acertarSequencia(e)
    e.ultimaSync = f.agora || p.agora || e.ultimaSync
  }, { doServidor: true })
}

function tirarDaFila(tipo: 'fichas' | 'produtores', id: string): void {
  mudar((e) => { e.pendentes[tipo] = e.pendentes[tipo].filter((y) => y !== id) }, { doServidor: true })
}

/**
 * Recusa definitiva: o servidor entendeu e disse não, e diria não de novo —
 * 4xx que não seja 401 (sessão caiu: entrar de novo resolve). O conflito e o
 * número repetido (409) são tratados antes, e nunca chegam aqui. 5xx não é
 * recusa de vez: tenta de novo. Mesma regra de pagamentos, extrato e catira.
 */
function recusaDeVez(e: Recusa): boolean {
  return e.status >= 400 && e.status < 500 && e.status !== 401
}

/** Sai da fila e fica marcada «recusada pelo servidor», com a frase. Nada se apaga. */
function marcarRecusa(tipo: 'fichas' | 'produtores', id: string, frase: string): void {
  mudar((e) => {
    e.pendentes[tipo] = e.pendentes[tipo].filter((y) => y !== id)
    e.recusadas[tipo][id] = frase
  }, { doServidor: true })
}

/**
 * Resolve um conflito. `escolha`:
 *  - lista de campos: fica a versão DESTE aparelho nesses campos (o resto, a
 *    do banco, que já está valendo). É o que a tela manda (R1A);
 *  - 'minha': todos os campos que divergem ficam com a deste aparelho;
 *  - 'banco': fica tudo como está (a do banco, com as pesagens já juntadas).
 * Conflito antigo (de antes da R1A, ou da adoção) não tem a lista do que
 * diverge: as listas se juntam agora, para nenhuma pesagem se perder.
 */
export function resolverConflito(indice: number, escolha: 'minha' | 'banco' | string[]): void {
  mudar((e) => {
    const c = e.conflitos[indice]
    if (!c) return
    const i = e.fichas.findIndex((x) => x.id === c.local.id)
    if (i < 0) {
      // a ficha não está neste aparelho (ex.: trazida pela adoção): «minha» a põe na fila
      if (escolha !== 'banco') {
        e.fichas.push({ ...c.local, revServidor: undefined, alteradaEm: agora() })
        if (!e.pendentes.fichas.includes(c.local.id)) e.pendentes.fichas.push(c.local.id)
      }
      e.conflitos.splice(indice, 1)
      return
    }
    const atual = e.fichas[i]!
    // conflito antigo: junta agora as listas (a do banco é a que está valendo aqui)
    const juncao = c.divergencias ? null : juntarFichas(atual, c.local)
    const base = juncao && escolha !== 'banco' ? juncao.junta : atual
    const divergencias = c.divergencias ?? juncao?.divergencias ?? []
    const campos = escolha === 'minha' ? divergencias.map((d) => d.campo) : escolha === 'banco' ? [] : escolha
    const nova = aplicarEscolhas(base, c.local, campos)
    if (!mesmoConteudo(nova, atual)) {
      e.fichas[i] = { ...nova, revServidor: atual.revServidor, alteradaEm: agora() }
      if (!e.pendentes.fichas.includes(atual.id)) e.pendentes.fichas.push(atual.id)
    }
    e.conflitos.splice(indice, 1)
  }, { doServidor: true })
}
